/**
 * Klasik mode levels (pure, no Phaser): the bot-graded pack, then designed chapters that reuse it with fresh seeds,
 * the star rule for the level map and the goal line on its level card. Tested in Node.
 */
import { CHAPTER_SHAPE, designLevel, type DesignSpec, type LevelDef, type SlotRole } from "@enjoyment/core";
import pack from "./levels/pack-1.json";

const LEVELS = (pack as { levels: LevelDef[] }).levels;

type Difficulty = NonNullable<LevelDef["difficulty"]>;

/**
 * Chapter design (@enjoyment/core designer.ts). Levels 1–40 are the pack as built: its saw-tooth (hard every 5th,
 * expert at 20 and 40) already puts the hard levels and the finale of levels 21–40 where the designer does. From 41
 * on, each slot's role picks the pack level to reuse: warm-ups and breathers an easy one, build-ups a medium one,
 * hard levels a hard one, the finale an expert one, from pack levels `floor`..`ceiling` (8: the first with crates,
 * past the board's introduction; 40: the pack's end). No twists: the pack has no variation knob that leaves every
 * class enough levels for a chapter without repeats.
 */
export const DESIGN: DesignSpec = { game: "mozaik", floor: 8, ceiling: 40, twists: [] };

export const ROLE_DIFFICULTY: Record<SlotRole, Difficulty> = {
  warmup: "easy",
  breather: "easy",
  build: "medium",
  hard: "hard",
  boss: "expert",
};

/** 0-based pack indices per difficulty class, pack levels floor..ceiling. */
const POOLS = new Map<Difficulty, number[]>();
LEVELS.forEach((l, i) => {
  if (i + 1 < DESIGN.floor || i + 1 > DESIGN.ceiling) return;
  const d = l.difficulty ?? "medium";
  POOLS.set(d, [...(POOLS.get(d) ?? []), i]);
});

/**
 * 0-based pack index that level `index` (0-based) plays: the pack itself first, then the designer's choice. A chapter
 * takes the next block of its class's levels for its slots of that class (no repeats inside a chapter; the same slot
 * gets another level in the next chapter).
 */
export function packIndexFor(index: number): number {
  const i = Math.max(0, Math.floor(index));
  if (i < LEVELS.length) return i;
  const d = designLevel(DESIGN, i + 1)!;
  const want = ROLE_DIFFICULTY[d.role];
  const pool = POOLS.get(want) ?? POOLS.get("medium")!;
  const slots = CHAPTER_SHAPE.map(([role]) => ROLE_DIFFICULTY[role]);
  const perChapter = slots.filter((x) => x === want).length;
  const before = slots.slice(0, d.index - 1).filter((x) => x === want).length;
  return pool[(d.chapter * perChapter + before) % pool.length]!;
}

/** Level at a 0-based index; every level reveals its own picture, and reused pack levels get a seed of their own. */
export function classicLevel(index: number): LevelDef {
  const i = Math.max(0, Math.floor(index));
  if (i < LEVELS.length) return { ...LEVELS[i]!, imageId: `img-${i + 1}` };
  const base = LEVELS[packIndexFor(i)]!;
  return { ...base, id: `${base.id}@${i + 1}`, seed: (base.seed + i * 104729) >>> 0, imageId: `img-${i + 1}` };
}

/**
 * Map stars from the share of moves left at the win. Bands follow the bot-graded difficulty so a
 * hard level is not a permanent one-star level; the greedy bot lands around two stars (tested).
 * Stars depend on the level only, never on the player (H3).
 */
const BANDS: Record<NonNullable<LevelDef["difficulty"]>, [three: number, two: number]> = {
  easy: [0.45, 0.25],
  medium: [0.3, 0.12],
  hard: [0.2, 0.07],
  expert: [0.15, 0.05],
};

export function classicStars(level: LevelDef, movesLeft: number): 1 | 2 | 3 {
  const [three, two] = BANDS[level.difficulty ?? "medium"];
  const left = level.moves > 0 ? movesLeft / level.moves : 0;
  return left >= three ? 3 : left >= two ? 2 : 1;
}

/** Tile colour names in COLORS order (GameScene). */
const COLOR_NAMES = {
  tr: ["kırmızı", "sarı", "yeşil", "mavi", "mor", "turuncu"],
  en: ["red", "yellow", "green", "blue", "purple", "orange"],
};

/** "Hedef: 24 kırmızı · 6 kasa · 26 hamle" for the level card. */
export function classicGoal(level: LevelDef, lang: "tr" | "en"): string {
  const parts = level.goals.map((g) => {
    if (g.type === "color") return `${g.count} ${COLOR_NAMES[lang][g.color] ?? ""}`.trim();
    if (g.type === "crate") return lang === "tr" ? `${g.count} kasa` : `${g.count} crates`;
    return lang === "tr" ? `${g.count} kare` : `${g.count} tiles`;
  });
  return lang === "tr" ? `Hedef: ${parts.join(" · ")} · ${level.moves} hamle` : `Goal: ${parts.join(" · ")} · ${level.moves} moves`;
}
